<template>
	<label v-if="label" class="form-label" :for="uid">
		<span>{{ t(label) }}{{ isRequired ? '* ' : '' }}</span>
		<efPopover v-if="popover" :title="t(`${popover}.title`)" :text="t(`${popover}.text`)" />
	</label>
	<div class="input-group" :class="{ 'has-validation': !isVisiblyValid }">
		<div v-if="!!slots['input-group-prepend']" class="input-group-text">
			<slot name="input-group-prepend"></slot>
		</div>
		<input
			:id="uid"
			v-model.trim="model"
			@focus="autofillGuard = false"
			@input="onInput"
			@blur="onBlur"
			@change="onChange"
			@paste="onPaste"
			:type="visible ? 'text' : 'password'"
			:maxlength="props.maxlength"
			:placeholder="props.placeholder"
			:class="['form-control', { 'is-invalid': !isVisiblyValid }, props.class]"
			:disabled="props.disabled"
			:readonly="(props.autocomplete === 'off' && autofillGuard) || props.readonly"
			:autocomplete="props.autocomplete"
			:name="label ?? uid"
		/>
		<button
			v-if="!props.hideToggle"
			type="button"
			class="input-group-text"
			:title="visible ? t('buttons.password_hide') : t('buttons.password_show')"
			@click="visible = !visible"
			:tabindex="-1"
		>
			<i :class="['fa fa-fw', visible ? 'fa-eye' : 'fa-eye-slash']" />
		</button>
		<div v-for="key in visibleValidations" :key="key" class="invalid-feedback w-100">
			{{ t(`validations.${key}`) }}
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, useSlots, type HTMLAttributes } from 'vue';
import { useI18n } from 'vue-i18n';

import efPopover from '@lib/components/efPopover.vue';
import useUid from '@lib/composables/useUid';
import useValidations from '@lib/composables/useValidations';

/*
 * States
 */
const { t } = useI18n();
const slots = useSlots();
const uid = useUid();
const visible = ref(false);
const autofillGuard = ref(true);

const props = withDefaults(
	defineProps<{
		readonly?: boolean;
		disabled?: boolean;
		label?: string;
		popover?: string;
		maxlength?: string | number;
		placeholder?: string;
		class?: HTMLAttributes['class'];
		validations?: Record<string, (item: ModelType) => boolean>;
		transform?: (value: ModelType) => ModelType;
		required?: boolean;
		/**
		 * `off` keeps the browser and password managers out, the field read only until it is
		 * focused. `current-password` or `new-password` lets them fill it or offer a new one.
		 */
		autocomplete?: 'off' | 'current-password' | 'new-password';
		/** Leaves out the eye button that shows the password in plain text. */
		hideToggle?: boolean;
	}>(),
	{
		readonly: false,
		disabled: false,
		required: false,
		autocomplete: 'off',
		hideToggle: false
	}
);

type ModelType = string | number | null;

const model = defineModel<ModelType>();

/*
 * Validation
 */
const { isRequired, visibleValidations, isVisiblyValid, touch } = useValidations<ModelType>({
	uid,
	value: model,
	validations: () => props.validations,
	required: () => props.required
});

/*
 * Emits
 */
const emit = defineEmits<{
	input: [value: ModelType];
	blur: [value: ModelType];
	change: [value: ModelType];
}>();

const onBlur = () => {
	touch();
	emit('blur', model.value ?? null);
};
const onChange = () => emit('change', model.value ?? null);

/*
 * Functions
 */
const onInput = (e: Event) => {
	const raw = (e.target as HTMLInputElement).value;
	model.value = props.transform ? props.transform(raw) : raw;
	emit('input', model.value);
};

const onPaste = (e: ClipboardEvent) => {
	e.preventDefault();
	const pasted = e.clipboardData?.getData('text') ?? '';
	model.value = props.transform ? props.transform(pasted) : pasted.trim();
};
</script>
